<template>
    <div>
        <div class="white-box">
            <transition
                name="opacity"
                mode="out-in"
            >
                <Skeletons v-if="isLoadingData" />
                <div
                    v-else
                    class="divide-y dividr-gray-200"
                >
                    <ul class="text-tip">
                        <li>定期定額投資金額申請，請同時填寫指定請款日，且需一併補收當期尚未繳納之計畫保費。</li>
                    </ul>
                    <Frame
                        v-model:isOpen="isOpenInvest"
                        title="投資設定"
                        button-position="mt-2.5 md:mt-3.5"
                        class="border-b border-gray-200"
                        @resetValues="cancelInvest"
                    >
                        <template #data>
                            <div
                                v-if="showData.showPayType"
                                class="flex justify-between pb-2.5 mb-2.5 border-bottom md:w-1/2 text-gray-700"
                            >
                                <p>繳別</p>
                                <p>
                                    {{ !showData.payTypeText ? '未指定' : showData.payTypeText }}
                                </p>
                            </div>
                            <div class="flex justify-between pb-2.5 mb-2.5 border-bottom md:w-1/2 text-gray-700">
                                <p>投資金額</p>
                                <p>{{ addComma(showData.amount) }}</p>
                            </div>
                            <div
                                v-if="showData.showPayDate"
                                class="w-full text-gray-700"
                                :class="{'border-bottom':!isOpenInvest}"
                            >
                                <div class="flex justify-between pb-2.5 md:w-1/2">
                                    <p>指定請款日</p>
                                    <p>
                                        {{ !showData.payDateList.length ? '未指定' : showData.payDateList.join('、') + '日' }}
                                    </p>
                                </div>
                            </div>
                        </template>
                        <template #inputs>
                            <div>
                                <div
                                    v-if="showData.showPayType"
                                    class="w-full flex justify-start pb-3.5 lg:pb-4"
                                >
                                    <label class="w-1/3 md:mr-1 label-base label-text">繳別</label>
                                    <div class="w-2/3 max-w-[15.625rem] lg:max-w-[12.25rem]">
                                        <Multiselect
                                            v-model="userData.payType"
                                            :options="mopList"
                                            class="select-base"
                                            placeholder="繳別"
                                            label="mopCH"
                                            :can-clear="false"
                                            :can-deselect="false"
                                            :disabled="isCancelService"
                                            @select="userData.errorMessage.payType = ''"
                                        />
                                        <p class="text-error text-right">
                                            {{ userData.errorMessage.payType }}
                                        </p>
                                    </div>
                                </div>
                                <div class="lg:flex pb-3.5 first:pt-0">
                                    <div
                                        class="flex justify-start lg:w-7/12 pb-3.5 lg:pb-0 "
                                        :class="{'mt-5 lg:mt-0':showData.amountMin !== 0}"
                                    >
                                        <label class="w-1/3 md:mr-1 label-base label-text">投資金額</label>
                                        <div class="w-full max-w-[15.625rem] lg:max-w-[12.25rem] relative">
                                            <input
                                                v-model.number="userData.amount"
                                                placeholder="請輸入投資金額"
                                                type="text"
                                                class="input-base w-full"
                                                inputmode="numeric"
                                                autocomplete="off"
                                                maxlength="7"
                                                :disabled="isCancelService"
                                                @input="validateAmount"
                                            >
                                            <p class="text-error text-right">
                                                {{ userData.errorMessage.amount }}
                                            </p>
                                            <ul
                                                v-if="showData.amountMin !== 0"
                                                class="text-tip mb-0 xs:leading-7 md:leading-[3rem] tips-position"
                                            >
                                                <li class="text-sm mb-0">
                                                    至少{{ addComma(showData.amountMin) }}元
                                                </li>
                                            </ul>
                                        </div>
                                    </div>
                                    <div
                                        v-if="showData.showPayDate"
                                        class="flex justify-start lg:w-5/12"
                                    >
                                        <label class="w-1/3 lg:w-auto md:mr-1 label-lineHight label-text">指定請款日</label>
                                        <div class="w-auto">
                                            <div class="w-full flex flex-wrap justify-start md:h-12">
                                                <Checkbox
                                                    v-for="(date, idx) in assignPayDateList"
                                                    :key="idx"
                                                    v-model:checkedValues="userData.payDateList"
                                                    :value="date"
                                                    :disabled="isCancelService"
                                                    :text="`${date}日`"
                                                    name="date"
                                                    class="mr-1 xs:mr-1.5 lg:mr-3.5 last:mr-0 flex-shrink-0 mb-1 md:mb-0"
                                                    @input="validatePayDate"
                                                />
                                            </div>
                                            <p class="text-error text-right">
                                                {{ userData.errorMessage.payDateList }}
                                            </p>
                                        </div>
                                    </div>
                                </div>
                                <div class="pt-3.5 border-t border-dashed border-gray-100">
                                    <!-- 資料庫無投資標的即未申請過 -->
                                    <Checkbox
                                        v-if="showData.regularInvestList.length!==0"
                                        v-model:checkedValues="isCancelService"
                                        :value="true"
                                        text="終止定期定額保費投資"
                                        class="items-baseline mb-0 mr-0"
                                        @input="clearAllErrMsg"
                                    />
                                </div>
                            </div>
                        </template>
                    </Frame>
                    <!-- 配置比例 -->
                    <div
                        v-if="!isCancelService"
                        class="pb-2"
                    >
                        <Frame
                            v-model:isOpen="isOpenPercent"
                            title="配置比例"
                            button-position="mt-2.5"
                            @resetValues="cancelPercent"
                        >
                            <template #data>
                                <CustomTable
                                    :is-border="false"
                                    :cursor-pointer="false"
                                    class="xs:text-lg"
                                >
                                    <template #thead>
                                        <tr class="hidden md:table-row">
                                            <td class="flex justify-between">
                                                <p>標的名稱</p>
                                                <p class="w-1/5 flex-shrink-0 px-3">
                                                    比例
                                                </p>
                                            </td>
                                        </tr>
                                    </template>
                                    <template #tbody>
                                        <tr
                                            v-for="(item, index) in showData.regularInvestList"
                                            :key="index"
                                            class="even:bg-white"
                                        >
                                            <td>
                                                <div class="flex justify-between">
                                                    <div>
                                                        {{ `${item.fundCode} ${item.fundName}` }}
                                                    </div>
                                                    <div class="w-1/5 flex-shrink-0 px-3">
                                                        {{ item.percent }}%
                                                    </div>
                                                </div>
                                                <div
                                                    v-if="item.fundMsg.length>0"
                                                    class="bg-gray-50 px-2.5 py-1.5 md:py-2.5 text-warning text-sm xs:text-base mt-2.5"
                                                >
                                                    {{ item.fundMsg }}
                                                </div>
                                            </td>
                                        </tr>
                                    </template>
                                </CustomTable>
                                <Nodata
                                    v-if="showData.regularInvestList.length===0"
                                    class="md:mt-3 mb-0"
                                >
                                    系統無資料
                                </Nodata>
                            </template>
                            <template #inputs>
                                <BankFundForm
                                    v-model:validInput="validInput"
                                    v-model:subtotal="subtotal"
                                    :dataBase="showData.regularInvestList"
                                    :data="userData.regularInvestList"
                                    :bankList="bankList"
                                    :fundBankList="fundBankList"
                                    autoAddRow
                                    class="border-b border-solid"
                                    @getValidResult="getValidResult"
                                />
                            </template>
                        </Frame>
                    </div>
                </div>
            </transition>
        </div>
        <Bottombar>
            <template #top>
                <div class="text-xs py-1">
                    剩餘可分配比例：
                </div>
                <div class="text-base xs:text-lg py-1">
                    {{ subtotal }}%
                </div>
            </template>
            <template #center>
                <div class="text-base py-2.5">
                    剩餘可分配比例： {{ subtotal }}%
                </div>
            </template>
            <template #right>
                <ButtonNextService
                    v-model:isSubmitOk="isSubmitOk"
                    :disabled="!hasChangeData"
                    @click="validGoNext"
                />
            </template>
        </Bottombar>
        <Loading :is-loading="isLoading" />
    </div>
</template>

<script>
import { ref, computed, inject, reactive, toRefs } from 'vue';
import { makeSort } from '@/utils/array/makeSort';
import { getInfoA4, getFundBankInfoA4, getMopInfoA4, submitA4 } from '@/apis/A4';
import { addComma } from '@/utils/number/addComma';
import { intMinMax } from '@/utils/rules/customRules';
import { useStore } from 'vuex';
import { useRoute } from 'vue-router';
import Checkbox from '@/components/checkboxs/Checkbox.vue';
import CustomTable from '@/components/table/Table.vue';
import ButtonNextService from '@/components/buttons/ButtonNextService.vue';
import Bottombar from '@/components/layout/Bottombar.vue';
import Frame from '@/components/collapse/CollapseFrame.vue';
import BankFundForm from '@/components/template/bankFund/BankFundForm.vue';
import dialogContnet from '@/json/dialogContent.js';
import Multiselect from '@vueform/multiselect';
import Skeletons from './Skeletons.vue';
import Loading from '@/components/Loading.vue';
import Nodata from '@/components/text/EmptyData.vue';
import _ from 'lodash';

export default {
    components: {
        Frame,
        Checkbox,
        CustomTable,
        BankFundForm,
        ButtonNextService,
        Bottombar,
        Multiselect,
        Skeletons,
        Loading,
        Nodata
    },
    setup() {
        const $vfm = inject('$vfm');
        const store = useStore();
        const route = useRoute();
        const { code } = route.meta;
        const state = reactive({
            isOpenInvest: false,
            isOpenPercent: false,
            isSubmitOk: false,
            isLoading: false,
            isLoadingData: false,
            validInput: false,
        });

        const data = reactive({
            bankList: [],
            fundBankList: [],
            mopList: [],
            showData: {},
            userData: {
                amount: 0,
                payDate1: '0',
                payDate2: '0',
                payDate3: '0',
                payDateList: [],
                payType: '',
                regularInvestList: [],
                errorMessage: {
                    amount: '',
                    payType: '',
                    payDateList: ''
                }
            },
            subtotal: 0,
        });
        const isCancelService = ref(false);
        const assignPayDateList = ['05', '15', '25'];
        const result = computed(() => {
            const vuexData = store.state.services.changeItemsResult[code];
            return vuexData ? _.cloneDeep(vuexData) : undefined;
        });

        /** 取得頁面資料 */
        const getData = async () => {
            state.isLoadingData = true;
            /** 投資標的公司及投資標的名稱下拉式選單資料 */
            await getFundBankInfoA4().then(res => {
                data.bankList = res.data.bankList.map(i => {
                    i.value = i.bankNo;
                    return i;
                });
                data.fundBankList = res.data.fundBankList.map(i => {
                    i.value = i.fundCode;
                    i.label = `${i.fundCode} ${i.fundName} (${i.fundTypeCH})`;
                    return i;
                });
            });

            /** user資料 */
            getInfoA4().then((res) => {
                if (res.data) {
                    /** 如果要秀繳別資料，就要打API */
                    if (res.data.showPayType) {
                        getMopInfoA4().then((res) => {
                            data.mopList = res.data.mopList.map(i => {
                                i.value = i.mop;
                                return i;
                            });
                        });
                    }
                    data.showData = _.cloneDeep(res.data);
                    const key = Object.keys(res.data);
                    const userDatakey = Object.keys(data.userData);
                    key.forEach(i => {
                        if (userDatakey.findIndex(j => i === j) !== -1) {
                            if (i === 'regularInvestList') {
                                data.userData[i] = resetForm(res.data[i]);
                            } else if (i === 'payDateList') {
                                data.userData[i] = res.data[i].map(i => assignPayDateList.find(j => j === i));
                            } else {
                                data.userData[i] = res.data[i];
                            }
                        }
                    });
                }
            }).finally(() => {
                state.isLoadingData = false;
            });
        };
        if (!result.value) {
            getData();
        } else {
            const {
                storeFundBankList,
                storeShowData,
                storeMopList,
                storeBankList,
                storeUserData,
                storeIsOpenInvest,
                storeIsOpenPercent,
                storeTerminate
            } = result.value;
            data.bankList = storeBankList;
            data.fundBankList = storeFundBankList;
            data.mopList = storeMopList;
            data.showData = _.cloneDeep(storeShowData);
            data.userData = _.cloneDeep(storeUserData);
            isCancelService.value = storeTerminate;
            state.isOpenInvest = storeIsOpenInvest;
            state.isOpenPercent = storeIsOpenPercent;
        }

        const validateAmount = (event) => {
            const amountMin = data.showData.amountMin;
            const { val, errorMessage } = intMinMax({
                value: event.target.value,
                minNum: amountMin,
                minMaxMsg: `投資金額須大於${addComma(amountMin)}元`,
            });
            data.userData.errorMessage.amount = errorMessage;
            event.target.value = val;
        };

        const validatePayDate = () => {
            const value = data.userData.payDateList;
            data.userData.errorMessage.payDateList = !value.length ? '請選擇指定請款日期' : '';
            data.userData.payDate1 = value.find(i => i === '05') ? '05' : '00';
            data.userData.payDate2 = value.find(i => i === '15') ? '15' : '00';
            data.userData.payDate3 = value.find(i => i === '25') ? '25' : '00';
        };

        /** 取消變更-投資設定 */
        const cancelInvest = () => {
            data.userData.amount = data.showData.amount;
            data.userData.payDate1 = data.showData.payDate1;
            data.userData.payDate2 = data.showData.payDate2;
            data.userData.payDate3 = data.showData.payDate3;
            data.userData.payDateList = _.cloneDeep(data.showData.payDateList);
            data.userData.payType = data.showData.payType;
            isCancelService.value = false;
            clearInvestErrMsg();
        };

        /** 取消變更配置比例 */
        const cancelPercent = () => {
            data.userData.regularInvestList = resetForm(data.showData.regularInvestList);
            clearPercentErrMsg();
        };

        /** 勾選取消申請，清除所有錯誤訊息 */
        const clearAllErrMsg = () => {
            if (isCancelService.value) {
                clearInvestErrMsg();
                clearPercentErrMsg();
            }
        };

        /** 清除投資設定錯誤訊息 */
        const clearInvestErrMsg = () => {
            data.userData.errorMessage.payType = '';
            data.userData.errorMessage.amount = '';
            data.userData.errorMessage.payDateList = '';
        };

        /** 清除配置比例錯誤訊息 */
        const clearPercentErrMsg = () => {
            data.userData.regularInvestList.forEach(i => {
                i.errorMessage.bankNo = '';
                i.errorMessage.fundCode = '';
                i.errorMessage.percent = '';
            });
        };

        /** 重設配置比例資料 */
        const resetForm = (baseList) => {
            const arr = _.cloneDeep(baseList);
            return arr.map(i => {
                i.dbData = true;
                i.errorMessage = {};
                if (i.disableType === 3) {
                    i.maxPercent = i.percent;
                }
                if (i.disableType === 0 || i.disableType === 2) {
                    i.disabledPercent = true;
                }
                return i;
            });
        };

        /** 製作只有fundCode percent 物件的陣列 */
        const makeArray = (ary) => {
            if (ary) {
                const list = ary.map(i => {
                    return {
                        fundCode: i.fundCode,
                        percent: i.percent
                    };
                });
                return list;
            }
        };

        const makeDataArray = (dataObj, examItem) => {
            const otherData = [];
            const dataKey = Object.keys(dataObj);
            dataKey.forEach(i => {
                if (examItem.findIndex(j => i === j) !== -1) {
                    otherData.push(dataObj[i]);
                }
            });
            return otherData;
        };

        /** 資料異動檢查 */
        const hasChangeData = computed(() => {
            let hasChangeData = false;
            if (isCancelService.value) {
                hasChangeData = true;
            } else {
                let showList = makeArray(data.showData.regularInvestList);
                let userList = makeArray(data.userData.regularInvestList);
                if (showList) showList = makeSort(showList, 'fundCode');
                if (userList) userList = makeSort(userList, 'fundCode');
                const examItem = ['amount', 'payDate1', 'payDate2', 'payDate3', 'payType'];
                const showDataOtherData = makeDataArray(data.showData, examItem);
                const userDataOtherData = makeDataArray(data.userData, examItem);
                const dbData = `${JSON.stringify(showDataOtherData)}${JSON.stringify(showList)}`;
                const changeData = `${JSON.stringify(userDataOtherData)}${JSON.stringify(userList)}`;
                hasChangeData = dbData !== changeData;
            }

            return hasChangeData;
        });

        /** 下一步 檢查錯誤與是否有修改資料 */
        const validGoNext = () => {
            if (hasChangeData.value) {
                /** 未勾選終止申請，繼續檢查其他欄位 */
                if (!isCancelService.value) {
                    state.validInput = true;
                } else {
                    submit();
                }
            }
        };

        const getValidResult = (hasFormError, list) => {
            // hasFormError=false ,繼續檢查其他欄位
            let hasErrorData = hasFormError;
            let params = null;
            if (!hasErrorData) {
                /* 如果有打開變更才檢查 */
                if (state.isOpenInvest) {
                    // 確認input是否為空
                    const { showPayType, showPayDate } = data.showData;
                    const { payType, amount, payDateList, errorMessage } = data.userData;
                    if (showPayType && !payType) data.userData.errorMessage.payType = '請選擇繳別';
                    if (!amount) data.userData.errorMessage.amount = '請輸入投資金額';
                    if (showPayDate && !payDateList.length) data.userData.errorMessage.payDateList = '請選擇指定請款日期';
                    hasErrorData = Object.values(errorMessage).some(i => i !== '');
                    if (hasErrorData) {
                        params = dialogContnet.validateMsg;
                        modalShow(params);
                    } else {
                        submit(list);
                    }
                } else if (!data.showData.regularInvestList.length) {
                    params = {
                        title: '提示',
                        content: '請變更投資設定。',
                        confirmButton: '確認',
                        cancelButton: null,
                        closeicon: true,
                    };
                    modalShow(params);
                } else {
                    submit(list);
                }
            }
        };

        const submit = (datalist) => {
            state.isLoading = true;
            const { amount, payDate1, payDate2, payDate3, payType } = data.userData;
            const returnData = {
                amount: null,
                payDate1: null,
                payDate2: null,
                payDate3: null,
                payType: null,
                regularInvestList: [],
                terminate: false
            };
            if (isCancelService.value) {
                returnData.terminate = isCancelService.value;
            } else {
                returnData.amount = amount;
                returnData.payDate1 = payDate1;
                returnData.payDate2 = payDate2;
                returnData.payDate3 = payDate3;
                returnData.payType = payType;
                returnData.regularInvestList = makeArray(datalist);
            }
            submitA4(returnData).then((res) => {
                const list = {
                    storeBankList: data.bankList,
                    storeFundBankList: data.fundBankList,
                    storeShowData: data.showData,
                    storeUserData: data.userData,
                    storeTerminate: isCancelService.value,
                    storeMopList: data.mopList,
                    storeIsOpenInvest: state.isOpenInvest,
                    storeIsOpenPercent: state.isOpenPercent,
                };
                store.dispatch('services/setChangeItemsResult', { code, value: list });
                state.isSubmitOk = true;
            }).finally(() => {
                state.isLoading = false;
            });
        };

        const modalShow = (params) => {
            $vfm.show({
                component: 'DialogBase',
                params,
                on: {
                    confirm() {
                        $vfm.hide('DialogBase');
                    },
                    closed() {
                        $vfm.hide('DialogBase');
                    }
                },
            });
        };

        return {
            ...toRefs(state),
            ...toRefs(data),
            isCancelService,
            addComma,
            hasChangeData,
            assignPayDateList,
            validGoNext,
            validateAmount,
            validatePayDate,
            clearAllErrMsg,
            cancelInvest,
            cancelPercent,
            getValidResult,
        };
    }
};
</script>
<style lang="scss" scoped>
label.label-lineHight {
    @apply leading-6 xs:leading-7 md:leading-[3rem];
}
.label-text {
    @apply flex-shrink-0 text-sm xs:text-base md:text-lg text-gray-900 max-w-[5.825rem];
}
.border-bottom {
    @apply border-b border-dashed border-gray-100;
}
.tips-position {
    @apply absolute top-[-1.35rem] lg:top-[0.875rem] lg:right-[-0.5rem] lg:translate-x-full;
}
</style>
